import { useEffect, useRef, useState } from "react";
import { BANNER_BG, clamp, overlaps } from "@/lib/bannerLayout";
import { SlotBox, BlockBox } from "@/components/banner/BannerItems";

function useWidth(ref) {
  const [w, setW] = useState(900);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

export function BannerCanvas({ ratio, slots, blocks, byId, nums, today, selected, onSelect, onCommit, readOnly, previewPath }) {
  const wrap = useRef(null);
  const W = useWidth(wrap);
  const h = clamp(W / ratio, 90, 420);
  const cw = h * ratio;
  const [live, setLive] = useState(null);
  const drag = useRef(null);

  const down = (kind, item) => (e, mode) => {
    if (readOnly) return;
    e.stopPropagation(); e.preventDefault();
    if (kind === "block" && item.locked) { onSelect({ kind, id: item.id }); return; }
    drag.current = { sx: e.clientX, sy: e.clientY, moved: false, box: null };
    const move = (ev) => {
      const d = drag.current;
      if (Math.abs(ev.clientX - d.sx) + Math.abs(ev.clientY - d.sy) > 3) d.moved = true;
      if (!d.moved) return;
      const dx = ((ev.clientX - d.sx) / cw) * 100;
      const dy = ((ev.clientY - d.sy) / h) * 100;
      const mid = (b) => (kind === "slot" ? { ...b, y: (100 - b.h) / 2 } : b);
      if (mode === "move") d.box = mid({ ...item, x: clamp(item.x + dx, 0, 100 - item.w), y: clamp(item.y + dy, 0, 100 - item.h) });
      else if (kind === "slot") {
        const k = Math.min(clamp(item.w + dx, 1, 100 - item.x) / item.w, 100 / item.h);
        d.box = mid({ ...item, w: Math.max(1, item.w * k), h: Math.max(4, item.h * k) });
      } else d.box = { ...item, w: clamp(item.w + dx, 0.5, 100 - item.x), h: clamp(item.h + dy, 6, 100 - item.y) };
      setLive({ kind, box: d.box });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = drag.current;
      drag.current = null;
      setLive(null);
      if (d.moved && d.box) onCommit(kind, d.box);
      else onSelect({ kind, id: item.id, click: true });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const cur = (kind, item) => (live && live.kind === kind && live.box.id === item.id ? live.box : item);
  const shownBlocks = blocks.map((b) => cur("block", b));
  return (
    <div ref={wrap} className="w-full overflow-x-auto pb-4" data-testid="banner-canvas-wrap">
      <div className="relative mx-auto rounded-md shadow-[inset_0_0_0_6px_rgba(255,255,255,0.08),0_10px_30px_-12px_rgba(0,0,0,0.45)]"
        style={{ width: cw, height: h, backgroundColor: BANNER_BG }} onPointerDown={() => !readOnly && onSelect(null)} data-testid="banner-canvas">
        <span className="absolute inset-x-0 top-1/2 border-t border-white/5 pointer-events-none" />
        {shownBlocks.map((b, i) => <BlockBox key={b.id} block={b} idx={i + 1} readOnly={readOnly} selected={selected?.kind === "block" && selected.id === b.id} onDown={down("block", blocks[i])} />)}
        {slots.map((s) => {
          const box = cur("slot", s);
          return <SlotBox key={s.id} slot={box} num={nums[s.id]} partner={byId[s.customer_id]} today={today} readOnly={readOnly} previewPath={previewPath}
            selected={selected?.kind === "slot" && selected.id === s.id} warn={shownBlocks.some((b) => overlaps(box, b))} onDown={down("slot", s)} />;
        })}
      </div>
    </div>
  );
}
